import React, { memo, useMemo } from 'react';
import ScheduleCell, { CellAssignment } from './ScheduleCell';
import { formatISODate } from 'util/dates';

/* =========================
 * Tipos
 * ========================= */

export interface EmployeeRow {
  id: number | string;
}

export interface ScheduleGridProps {
  employees: EmployeeRow[];
  dates: Date[];
  assignments: Map<string, CellAssignment[]>;
  rowHeights: Map<number | string, number>;
  columnCount: number;
  gridScrollRef: React.RefObject<HTMLDivElement>;
  isCopyModeActive: boolean;
  onGridScroll: (left: number) => void;
  onAddShift: (spotId: number | string, dateISO: string) => void;
  onEditShift: (shiftId: number) => void;
  onDeleteShift: (shiftId: number) => void;
  onCopyShift: (shiftId: number) => void;
}

/* =========================
 * Component
 * ========================= */

function ScheduleGrid({
  employees,
  dates,
  assignments,
  rowHeights,
  columnCount,
  gridScrollRef,
  isCopyModeActive,
  onGridScroll,
  onAddShift,
  onEditShift,
  onDeleteShift,
  onCopyShift,
}: ScheduleGridProps) {
  const dateISOs = useMemo(
    () => dates.map(formatISODate),
    [dates]
  );

  const MIN_COL_WIDTH = 250;

  return (
    <div
      className="schGrid"
    >

      <div
        className="schGrid__viewport"
        ref={gridScrollRef}
        onScroll={(e) => onGridScroll(e.currentTarget.scrollLeft)}
        style={{
          cursor: isCopyModeActive ? 'crosshair' : 'default'
        }}
      >
        {/* Spacer superior virtualización */}
        <div/>

        {employees.map((emp) => (
          <div
            key={emp.id}
            className="schRow"
            style={{ height: rowHeights.get(emp.id), display: 'grid', gridTemplateColumns: `repeat(${columnCount}, minmax(${MIN_COL_WIDTH}px, 1fr))`
 }}
          >
            {dateISOs.map((iso) => {
              const key = `${emp.id}__${iso}`;
              const cellAssignments = assignments.get(key) ?? [];

              return (
                <ScheduleCell
                  key={`${emp.id}__${iso}`}
                  spotId={emp.id}
                  dateISO={iso}
                  assignments={cellAssignments}
                  rowHeight={rowHeights.get(emp.id)!}
                  onAddShift={onAddShift}
                  onEditShift={onEditShift}
                  onDeleteShift={onDeleteShift}
                  onCopyShift={onCopyShift}
                />
              );
            })}

          </div>
        ))}
      </div>
    </div>
  );
}

/* =========================
 * Memo export
 * ========================= */

export default memo(ScheduleGrid);
